import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchSkinShop } from '../_helpers/skinShop.js';

/**
 * The skins shop's data (ENG-823): one read of `GET /public/skins`, with the
 * session when there is one so owned items say so. The tabs filter on the
 * client, so switching them reads nothing.
 *
 * Returns `{ status, items, error, retry, markOwned }`:
 *   - `status` is 'loading' | 'ready' | 'error'; `items` stays the last list
 *     read while a re-read runs.
 *   - `retry()` reads again (the error state's Retry, and after a purchase
 *     for fresh edition counts).
 *   - `markOwned(id)` flips an item to owned at once, before the re-read.
 *   - `markSoldOut(id)` flips an item to sold out at once (a 409
 *     `skin_sold_out`, ENG-833), before the re-read.
 *   - `setPrice(id, priceCents)` shows the price the backend says it is now
 *     (a price-changed 409, ENG-827), before the re-read; null = not for sale.
 *     Any sale on the item is dropped until the re-read says otherwise.
 */
export const useSkinShop = () => {
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState({ status: 'loading', items: null, error: null });
  const readRef = useRef(0);

  useEffect(() => {
    readRef.current += 1;
    const read = readRef.current;
    setState((current) => ({ status: 'loading', items: current.items, error: null }));
    fetchSkinShop()
      .then((items) => { if (readRef.current === read) setState({ status: 'ready', items, error: null }); })
      .catch((error) => { if (readRef.current === read) setState((current) => ({ status: 'error', items: current.items, error })); });
  }, [attempt]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);
  const markOwned = useCallback((id) => {
    if (!id) return;
    setState((current) => (current.items
      ? { ...current, items: current.items.map((item) => (item.id === id ? { ...item, owned: true } : item)) }
      : current));
  }, []);

  // A 409 skin_sold_out (ENG-833): the item says Sold out before the re-read lands.
  const markSoldOut = useCallback((id) => {
    if (!id) return;
    setState((current) => (current.items
      ? { ...current, items: current.items.map((item) => (item.id === id ? { ...item, soldOut: true } : item)) }
      : current));
  }, []);

  // The backend's current price from a price-guard 409 (ENG-825), as sent; null = no longer for sale.
  const setPrice = useCallback((id, priceCents) => {
    if (!id) return;
    const forSale = Number.isSafeInteger(priceCents) && priceCents >= 0;
    setState((current) => (current.items
      ? {
        ...current,
        items: current.items.map((item) => (item.id === id
          // The sale shown may be the one that just ended (ENG-832); the re-read brings back any still on.
          ? { ...item, forSale, priceCents: forSale ? priceCents : null, sale: null }
          : item)),
      }
      : current));
  }, []);

  return { status: state.status, items: state.items, error: state.error, retry, markOwned, markSoldOut, setPrice };
};

export default useSkinShop;
